<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <p class="card-title px-0 mb-2">Atividade dos disparadores</p>
      </v-card-title>
      <v-card-subtitle>
        <p class="texto-corrido">
          Nesse gráfico você pode ver em quais horários seus números estiveram conectados com o Whatsapp web vinculado a Mercafacil. Esses status são agrupados
          a cada 5 minutos, se seu número esteve offline dentro desse período, ele será considerado como instável.
        </p>
      </v-card-subtitle>
      <v-card-text>
        <v-row v-if="chartLoader" justify="center" style="height: 465px">
          <v-col cols="1" style="padding-top: 150px">
            <v-progress-circular size="60" indeterminate color="primary" />
          </v-col>
        </v-row>
        <v-row v-else-if="voidChart" justify="center" style="height: 465px">
          <v-col class="texto-corrido text-center" cols="4" style="padding-top: 150px"> Sua plataforma não possui nenhum disparador nesse dia. </v-col>
        </v-row>
        <apexchart v-else ref="chart" type="rangeBar" height="250" :options="chartOptions" :series="series" />
        <v-row no-gutters>
          <v-spacer />
          <mf-tooltip top max-width="192px">
            <template #activator="{ on }">
              <!-- blocking access before 2021-05-06 because data isn't right before that -->
              <mf-button
                icon="arrow_left"
                type="icon"
                size="sm"
                color="cinza-0"
                :disabled="DateTime.fromFormat(dateSelected, 'yyyy-MM-dd').diff(DateTime.fromFormat('2021-05-06', 'yyyy-MM-dd'), ['days']).days <= 0"
                @click="changeDate(-1)"
              />
            </template>
            <span>Dia anterior</span>
          </mf-tooltip>
          <span class="date-selected mt-1">{{ DateTime.fromFormat(dateSelected, 'yyyy-MM-dd').setLocale('pt-BR').toFormat("d 'de' MMMM, yyyy") }}</span>
          <mf-tooltip top max-width="192px">
            <template #activator="{ on }">
              <mf-button icon="arrow_right" type="icon" size="sm" color="cinza-0" :disabled="disableChangeDate" @click="changeDate(1)" />
            </template>
            <span>Próximo dia</span>
          </mf-tooltip>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<script>
import gql from 'graphql-tag'
import findLastIndex from 'lodash/findLastIndex'
import { DateTime } from 'luxon'
import { phoneNumberFormat } from '@/helpers/phoneNumberFormat.js'

const QUERY_GET_SENDERS_STATUS_HISTORY = gql`
  query SendersStatusHistory($date: String!) {
    sendersStatusHistory(date: $date) {
      time
      senders_status {
        sender {
          name
          number
        }
        status
      }
    }
  }
`

export default {
  name: 'TriggersActivityChart',
  data: () => ({
    DateTime: DateTime,
    dateSelected: '',
    timeout: {},
    chartLoader: true,
    voidChart: false,
    series: [],
    chartOptions: {
      tooltip: {
        enabled: false,
      },
      colors: ['#0FC94D', '#E03211', '#D2D6D9', '#fff'],
      chart: {
        animations: {
          enabled: false,
        },
        height: 800,
        type: 'rangeBar',
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          rangeBarGroupRows: true,
          horizontal: true,
          barHeight: '20%',
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        labels: {
          maxWidth: 300,
        },
      },
      stroke: {
        width: 0,
      },
      fill: {
        type: 'solid',
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'left',
        showForSingleSeries: true,
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
        formatter: function (value) {
          return value !== 'empty' ? value : ''
        },
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
    },
  }),
  computed: {
    disableChangeDate() {
      return DateTime.now().toFormat('yyyy-MM-dd') === this.dateSelected
    },
  },
  mounted() {
    this.dateSelected = DateTime.now().toFormat('yyyy-MM-dd')
    this.getChartData()
  },
  methods: {
    formatNumber(number) {
      return phoneNumberFormat(number)
    },
    changeDate(day) {
      if (day === 1 && DateTime.now().toFormat('yyyy-MM-dd') === this.dateSelected) return
      // blocking access before 2021-05-06 because data isn't right before that
      if (day === -1 && DateTime.fromFormat(this.dateSelected, 'yyyy-MM-dd').diff(DateTime.fromFormat('2021-05-06', 'yyyy-MM-dd'), ['days']).days <= 0) return
      this.dateSelected = DateTime.fromFormat(this.dateSelected, 'yyyy-MM-dd').plus({ days: day }).toFormat('yyyy-MM-dd')
      clearTimeout(this.timeout)
      this.chartLoader = true
      this.timeout = setTimeout(() => {
        this.getChartData()
      }, 1000)
    },
    async getChartData() {
      try {
        const { data: sendersStatus } = await this.$apollo.query({
          query: QUERY_GET_SENDERS_STATUS_HISTORY,
          variables: {
            date: this.dateSelected,
          },
          fetchPolicy: 'no-cache',
        })

        if (sendersStatus.sendersStatusHistory[0].senders_status.length === 0) {
          this.chartLoader = false
          this.voidChart = true
          return
        }

        const senders = []
        const chart = sendersStatus.sendersStatusHistory.reduce(
          (series, curr) => {
            curr.senders_status.forEach((senderInfo) => {
              const senderName = senderInfo.sender.name || this.formatNumber(senderInfo.sender.number)
              if (!senders.includes(senderName)) {
                senders.push(senderName)
              }
              const statusPosition = series.findIndex((serie) => {
                let status
                if (!senderInfo.status) status = 'empty'
                if (senderInfo.status === 'INACTIVE') status = 'Indisponível'
                if (senderInfo.status === 'ONLINE') status = 'Disponível'
                if (senderInfo.status === 'UNSTABLE') status = 'Instável'
                return serie.name === status
              })
              const triggersArray = series[statusPosition].data.filter((trigger) => trigger.x === senderName)
              if (
                triggersArray.length > 0 &&
                triggersArray[triggersArray.length - 1].y[1] === DateTime.fromFormat(`${this.dateSelected} ${curr.time}`, 'yyyy-MM-dd HH:mm').ts
              ) {
                const triggerIdx = findLastIndex(series[statusPosition].data, (item) => item.x === senderName)
                series[statusPosition].data[triggerIdx].y[1] = DateTime.fromFormat(`${this.dateSelected} ${curr.time}`, 'yyyy-MM-dd HH:mm').plus({
                  minutes: 5,
                }).ts
              } else {
                series[statusPosition].data.push({
                  x: senderName,
                  y: [
                    DateTime.fromFormat(`${this.dateSelected} ${curr.time}`, 'yyyy-MM-dd HH:mm').ts,
                    DateTime.fromFormat(`${this.dateSelected} ${curr.time}`, 'yyyy-MM-dd HH:mm').plus({ minutes: 5 }).ts,
                  ],
                })
              }
            })
            return series
          },
          [
            {
              name: 'Disponível',
              data: [],
            },
            {
              name: 'Instável',
              data: [],
            },
            {
              name: 'Indisponível',
              data: [],
            },
            {
              name: 'empty',
              data: [],
            },
          ]
        )
        this.chartOptions.chart.height = Math.max(Math.min(senders.length * 50, 800), 250)

        this.chartLoader = false
        if (!this.$refs.chart) await new Promise((resolve) => setTimeout(resolve, 1)) // timeout to ensure that the chart exists
        const chartOptions = {
          ...this.chartOptions,
          ...{
            xaxis: {
              min: DateTime.fromFormat(`${this.dateSelected} 04:00`, 'yyyy-MM-dd HH:mm').ts,
              max: DateTime.fromFormat(`${this.dateSelected} 23:59`, 'yyyy-MM-dd HH:mm').ts,
            },
          },
        }

        this.$refs.chart.updateOptions(chartOptions)
        this.$refs.chart.updateSeries(chart)
      } catch {
        this.voidChart = true
        this.chartLoader = false
      }
    },
  },
}
</script>
<style lang="scss">
.date-selected {
  @extend .table-card-title-whats !optional;
  padding: 0 4px;
}
</style>
